import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { RulesApiService } from 'src/app/core/services/rules-api.service';
import { DeleteConfirmModalComponent } from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';
import { EntityCatalogComponent } from 'src/app/shared/components/entity-catalog/entity-catalog.component';
import { entityText } from 'src/app/shared/components/entity-catalog/entity-text.util';
import {
  buildDeletedCatalog,
  formatDeletedAt,
} from 'src/app/shared/components/entity-catalog/entity-catalog-deleted.util';
import { EntityRecord } from 'src/app/shared/components/entity-catalog/entity-catalog.types';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import { LLM_MODEL_CATALOG } from './llm-models.catalog';

type LlmModelView = 'active' | 'deleted';

/** The catalog declares the screen; this component only loads and persists. */
@Component({
  selector: 'app-llm-models',
  standalone: true,
  imports: [
    CommonModule,
    EntityCatalogComponent,
    PageSurfaceHeaderComponent,
    DeleteConfirmModalComponent,
    RestoreConfirmModalComponent,
  ],
  templateUrl: './llm-models.component.html',
})
export class LlmModelsComponent implements OnInit {
  private readonly api = inject(RulesApiService);

  readonly activeCatalog = LLM_MODEL_CATALOG;
  readonly deletedCatalog = buildDeletedCatalog(LLM_MODEL_CATALOG);
  readonly toolbarTabs = [{ key: 'deleted', label: 'Deleted' }];

  view: LlmModelView = 'active';
  records: EntityRecord[] = [];
  loading = false;
  saving = false;
  error = '';

  showDeleteModal = false;
  deleteError = '';
  readonly deleteLoading = signal(false);
  selectedForDelete: EntityRecord | null = null;

  showRestoreModal = false;
  readonly restoreLoading = signal(false);
  selectedForRestore: EntityRecord | null = null;

  ngOnInit(): void {
    this.load();
  }

  get catalogView() {
    return this.view === 'deleted' ? this.deletedCatalog : this.activeCatalog;
  }

  get activeToolbarTab(): string {
    return this.view === 'deleted' ? 'deleted' : '';
  }

  onToolbarTabChange(key: string): void {
    this.view = this.view === key ? 'active' : (key as LlmModelView);
    this.error = '';
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    const request$ =
      this.view === 'deleted' ? this.api.listDeletedLlmModels() : this.api.listLlmModels();

    request$
      .pipe(assertSuccessEnvelope(), finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          const rows = (res.data ?? []) as EntityRecord[];
          this.records =
            this.view === 'deleted'
              ? rows.map((row) => ({ ...row, deleted_at: formatDeletedAt(row['deleted_at']) }))
              : rows;
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onSave(event: { record: EntityRecord; isNew: boolean }): void {
    const body = this.toRequestBody(event.record);
    const request$ = event.isNew
      ? this.api.createLlmModel(body)
      : this.api.updateLlmModel(Number(event.record['id']), body);

    this.saving = true;
    this.error = '';
    request$
      .pipe(assertSuccessEnvelope(), finalize(() => (this.saving = false)))
      .subscribe({
        next: () => this.load(),
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onDelete(row: EntityRecord): void {
    this.deleteError = '';
    this.selectedForDelete = row;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedForDelete = null;
    this.deleteError = '';
  }

  submitDelete(): void {
    const row = this.selectedForDelete;
    if (!row) return;

    const id = Number(row['id']);
    if (!Number.isFinite(id) || id <= 0) return;

    this.deleteLoading.set(true);
    this.deleteError = '';
    this.api
      .deleteLlmModel(id)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.deleteLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.load();
        },
        error: (err) => (this.deleteError = resolveApiErrorMessage(err)),
      });
  }

  onRestore(row: EntityRecord): void {
    this.selectedForRestore = row;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selectedForRestore = null;
  }

  submitRestore(): void {
    const row = this.selectedForRestore;
    if (!row) return;

    const id = Number(row['id']);
    if (!Number.isFinite(id) || id <= 0) return;

    this.restoreLoading.set(true);
    this.error = '';
    this.api
      .restoreLlmModel(id)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.restoreLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeRestoreModal();
          this.view = 'active';
          this.load();
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  get deleteEntityName(): string {
    return this.entityLabel(this.selectedForDelete);
  }

  get restoreEntityName(): string {
    return this.entityLabel(this.selectedForRestore);
  }

  private entityLabel(row: EntityRecord | null): string {
    if (!row) return '';
    const provider = entityText(row['provider'], 'openai').trim();
    const model = entityText(row['model_name']).trim();
    return model ? `${provider}/${model}` : provider;
  }

  /** Drops empties so an untouched optional field is omitted rather than sent as ''. */
  private toRequestBody(record: EntityRecord): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    for (const field of this.activeCatalog.fields) {
      const value = record[field.key];
      if (field.kind === 'boolean') {
        body[field.key] = !!value;
        continue;
      }
      if (value === '' || value == null) continue;
      body[field.key] = value;
    }
    delete body['id'];
    return body;
  }
}
